import React from 'react';
import { Building2, UserCheck, UserPlus, Bell, FileText, X } from 'lucide-react';
import { cn } from '@/utils/cn';
import { navTestId } from '@/utils/testId';
import { formatNotification, formatRelativeTime } from './formatNotification';
import type { NotificationItem as NotificationItemType } from './types';

interface NotificationItemProps {
  notification: NotificationItemType;
  onClick: (notification: NotificationItemType) => void;
  onDismiss: (id: string) => void;
}

const iconFor = (type: string): React.ReactNode => {
  switch (type) {
    case 'approval.user.pending':
      return <UserPlus size={16} />;
    case 'approval.company.pending':
      return <Building2 size={16} />;
    case 'approval.employee.pending':
      return <UserCheck size={16} />;
    case 'pdf.generation.done':
    case 'pdf.generation.failed':
      return <FileText size={16} />;
    default:
      return <Bell size={16} />;
  }
};

const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  onClick,
  onDismiss,
}) => {
  const { title, description } = formatNotification(notification.type, notification.data);

  return (
    <div
      role="button"
      tabIndex={0}
      data-testid={`notification-item-${navTestId(notification.type)}`}
      onClick={() => onClick(notification)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick(notification);
        }
      }}
      className={cn(
        'group relative flex gap-3 px-3 py-2.5 cursor-pointer transition-colors hover:bg-neutral-50',
        !notification.isRead && 'bg-primary-50/40'
      )}
    >
      <div
        className={cn(
          'mt-0.5 h-8 w-8 shrink-0 rounded-full flex items-center justify-center',
          notification.isRead
            ? 'bg-neutral-100 text-neutral-500'
            : 'bg-primary-100 text-primary-600'
        )}
      >
        {iconFor(notification.type)}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-start gap-2">
          <p
            className={cn(
              'text-sm leading-tight',
              notification.isRead ? 'text-neutral-600' : 'font-medium text-neutral-900'
            )}
          >
            {title}
          </p>
          {!notification.isRead && (
            <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-primary-600" />
          )}
        </div>
        {description && <p className="mt-0.5 truncate text-xs text-neutral-500">{description}</p>}
        <p className="mt-1 text-[10px] text-neutral-400">
          {formatRelativeTime(notification.createdAt)}
        </p>
      </div>

      <button
        type="button"
        data-testid="notification-dismiss-btn"
        onClick={(e) => {
          e.stopPropagation();
          onDismiss(notification.id);
        }}
        className="absolute right-2 top-2 hidden h-6 w-6 items-center justify-center rounded text-neutral-400 hover:bg-neutral-200 hover:text-neutral-700 group-hover:flex"
        aria-label="Odstranit notifikaci"
      >
        <X size={13} />
      </button>
    </div>
  );
};

export default NotificationItem;
